/**
 * Typed, validated access to build-time environment configuration.
 *
 * BUILD-PLAN §49 forbids hard-coding API URLs and domains. Reading them
 * through this module (rather than sprinkling import.meta.env across the
 * codebase) gives one place to validate them, and makes a misconfigured
 * deployment fail at startup with a clear message instead of producing
 * confusing 404s against the wrong host.
 */

function required(key: string, value: string | undefined, fallback?: string): string {
  const resolved = value ?? fallback;

  if (!resolved) {
    throw new Error(
      `Missing required environment variable ${key}. Add it to frontend/.env — see .env.example.`,
    );
  }

  return resolved.replace(/\/+$/, ''); // Normalise away any trailing slash.
}

export const env = {
  /**
   * Base URL of the API, e.g. https://api.glanzluxe.com
   *
   * The localhost fallback is DEVELOPMENT ONLY, and that restriction matters.
   * A production bundle built without VITE_API_URL would otherwise ship
   * pointing at localhost: every request fails, every visitor sees a generic
   * error, and nothing anywhere says the deployment was misconfigured. Worse,
   * the string is baked into the bundle for anyone to read.
   *
   * Failing loudly at startup instead is deliberate — a misconfigured deploy
   * should be caught by the first smoke test, not by customers.
   */
  apiUrl: required(
    'VITE_API_URL',
    import.meta.env['VITE_API_URL'],
    import.meta.env.DEV ? 'http://localhost:8000' : undefined,
  ),

  appName: import.meta.env['VITE_APP_NAME'] ?? 'Glanz Luxe',

  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
} as const;
